/**
 * Top context bar showing current focus and context tabs
 *
 * Navigation items are generated dynamically from module registry.
 * Hidden modules will not appear in the navigation.
 */

import React, { useState, useEffect } from "react";
import {
  Space,
  Button,
  Tooltip,
  Badge,
  Typography,
  Input,
  Popover,
  Tag,
  Empty,
} from "antd";
import axios from "axios";
import {
  BankOutlined,
  UserOutlined,
  TeamOutlined,
  DollarOutlined,
  SearchOutlined,
  HomeOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  MailOutlined,
  FolderOutlined,
  CalendarOutlined,
  CloseOutlined,
  ShoppingCartOutlined,
  AppstoreOutlined,
  ShoppingOutlined,
  TrophyOutlined,
  SolutionOutlined,
  AuditOutlined,
  ProjectOutlined,
  CheckSquareOutlined,
  FlagOutlined,
  FileProtectOutlined,
  ReadOutlined,
  CheckCircleOutlined,
  ReconciliationOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  NodeIndexOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useContextStore } from "@/hooks/useContextStore";
import { CalendarWidget } from "./CalendarWidget";
import { getNavigationItems } from "@/modules/entities/routeConfig";
import { MessagingInbox } from "@/components/messaging/MessagingInbox";

// Icon mapping for dynamic navigation
const iconComponents: Record<string, React.ReactNode> = {
  BankOutlined: <BankOutlined />,
  UserOutlined: <UserOutlined />,
  TeamOutlined: <TeamOutlined />,
  TrophyOutlined: <TrophyOutlined />,
  DollarOutlined: <DollarOutlined />,
  CalendarOutlined: <CalendarOutlined />,
  FolderOutlined: <FolderOutlined />,
  ShoppingCartOutlined: <ShoppingCartOutlined />,
  ShoppingOutlined: <ShoppingOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  FileOutlined: <FileTextOutlined />,
  FileProtectOutlined: <FileProtectOutlined />,
  ReadOutlined: <ReadOutlined />,
  CheckCircleOutlined: <CheckCircleOutlined />,
  ReconciliationOutlined: <ReconciliationOutlined />,
  SolutionOutlined: <SolutionOutlined />,
  AuditOutlined: <AuditOutlined />,
  ProjectOutlined: <ProjectOutlined />,
  CheckSquareOutlined: <CheckSquareOutlined />,
  FlagOutlined: <FlagOutlined />,
  WarningOutlined: <WarningOutlined />,
  SafetyCertificateOutlined: <SafetyCertificateOutlined />,
  NodeIndexOutlined: <NodeIndexOutlined />,
  DatabaseOutlined: <DatabaseOutlined />,
};

const { Text } = Typography;

export const ContextBar: React.FC = () => {
  const navigate = useNavigate();
  const {
    activeContextId,
    contexts,
    clearActiveContext,
    removeEntityFromContext,
  } = useContextStore();

  const activeContext = activeContextId ? contexts.get(activeContextId) : null;

  // Resolve entity IDs to display names
  const [entityNames, setEntityNames] = useState<Record<string, Record<string, string>>>({
    accounts: {},
    contacts: {},
    opportunities: {},
  });

  useEffect(() => {
    if (!activeContext?.entities) return;

    const fetchNames = async () => {
      const { accounts = [], contacts = [], opportunities = [] } = activeContext.entities!;

      if (accounts.length > 0) {
        try {
          const res = await axios.post("/api/direct/query", {
            query: `SELECT id, accountname FROM Accounts WHERE id = '${accounts.join("' OR id = '")}' LIMIT 100;`,
          });
          const map: Record<string, string> = {};
          (res.data?.result || []).forEach((a: any) => { map[a.id] = a.accountname; });
          setEntityNames((prev) => ({ ...prev, accounts: { ...prev.accounts, ...map } }));
        } catch { /* ignore */ }
      }

      if (contacts.length > 0) {
        try {
          const res = await axios.post("/api/direct/query", {
            query: `SELECT id, firstname, lastname FROM Contacts WHERE id = '${contacts.join("' OR id = '")}' LIMIT 100;`,
          });
          const map: Record<string, string> = {};
          (res.data?.result || []).forEach((c: any) => { map[c.id] = `${c.firstname || ""} ${c.lastname || ""}`.trim(); });
          setEntityNames((prev) => ({ ...prev, contacts: { ...prev.contacts, ...map } }));
        } catch { /* ignore */ }
      }

      if (opportunities.length > 0) {
        try {
          const res = await axios.post("/api/direct/query", {
            query: `SELECT id, potentialname FROM Potentials WHERE id = '${opportunities.join("' OR id = '")}' LIMIT 100;`,
          });
          const map: Record<string, string> = {};
          (res.data?.result || []).forEach((o: any) => { map[o.id] = o.potentialname; });
          setEntityNames((prev) => ({ ...prev, opportunities: { ...prev.opportunities, ...map } }));
        } catch { /* ignore */ }
      }
    };

    fetchNames();
  }, [activeContext?.entities]);

  // Focus the assistant panel's input (and expand if collapsed)
  const focusAssistant = () => {
    // Dispatch event to expand the assistant panel if collapsed
    window.dispatchEvent(new CustomEvent("expand-assistant-panel"));

    // Small delay to allow panel expansion animation
    setTimeout(() => {
      const assistantInput = document.querySelector(
        '[data-assistant-input="true"]'
      ) as HTMLTextAreaElement;
      if (assistantInput) {
        assistantInput.focus();
      }
    }, 100);
  };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      {/* Left: Command Bar & Quick Actions */}
      <Space size="middle">
        <Input
          placeholder="Ask AI anything..."
          prefix={<ThunderboltOutlined style={{ color: "#6366f1" }} />}
          style={{
            width: 260,
            backgroundColor: "#f8f9fa",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
          }}
          onClick={focusAssistant}
          readOnly
        />
        <Space size="small">
          <CalendarWidget />
          <Tooltip title="Prepare customer brief">
            <Button
              type="text"
              size="small"
              icon={<FileTextOutlined />}
              onClick={focusAssistant}
            />
          </Tooltip>
          <Tooltip title="Email">
            <Button
              type="text"
              size="small"
              icon={<MailOutlined />}
              onClick={() => navigate("/emails")}
            />
          </Tooltip>
        </Space>
      </Space>

      {/* Center: Spacer - context info is shown in the right panel */}
      <div style={{ flex: 1 }} />

      {/* Right: Active Context (Lens) + Navigation & Actions */}
      <Space>
        {/* Active Context (Lens) with management popover */}
        {activeContext && (
          <Popover
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>{activeContext.label}</span>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<CloseOutlined />}
                  onClick={clearActiveContext}
                />
              </div>
            }
            content={
              <div style={{ minWidth: 200, maxWidth: 300 }}>
                {/* Accounts */}
                {(activeContext.entities?.accounts?.length ?? 0) > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      <BankOutlined /> Accounts
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      {activeContext.entities?.accounts?.map((id) => (
                        <Tag
                          key={id}
                          closable
                          color="blue"
                          onClose={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeEntityFromContext(
                              activeContext.id,
                              "accounts",
                              id
                            );
                          }}
                          style={{ marginBottom: 4 }}
                        >
                          {entityNames.accounts[id] || id}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contacts */}
                {(activeContext.entities?.contacts?.length ?? 0) > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      <UserOutlined /> Contacts
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      {activeContext.entities?.contacts?.map((id) => (
                        <Tag
                          key={id}
                          closable
                          color="green"
                          onClose={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeEntityFromContext(
                              activeContext.id,
                              "contacts",
                              id
                            );
                          }}
                          style={{ marginBottom: 4 }}
                        >
                          {entityNames.contacts[id] || id}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                {/* Opportunities */}
                {(activeContext.entities?.opportunities?.length ?? 0) > 0 && (
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      <DollarOutlined /> Opportunities
                    </Text>
                    <div style={{ marginTop: 4 }}>
                      {activeContext.entities?.opportunities?.map((id) => (
                        <Tag
                          key={id}
                          closable
                          color="gold"
                          onClose={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeEntityFromContext(
                              activeContext.id,
                              "opportunities",
                              id
                            );
                          }}
                          style={{ marginBottom: 4 }}
                        >
                          {entityNames.opportunities[id] || id}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty state */}
                {!activeContext.entities?.accounts?.length &&
                  !activeContext.entities?.contacts?.length &&
                  !activeContext.entities?.opportunities?.length && (
                    <Empty
                      description="No entities in context"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      style={{ margin: "8px 0" }}
                    />
                  )}
              </div>
            }
            trigger="click"
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              style={{
                background: activeContext.color + "15",
                borderRadius: 6,
                padding: "4px 12px",
                marginRight: 8,
                transition: "all 0.15s ease",
              }}
            >
              <Space size={4}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: activeContext.color,
                    display: "inline-block",
                  }}
                />
                <Text style={{ fontSize: 12, fontWeight: 500 }}>
                  {activeContext.label}
                </Text>
                <Badge
                  count={
                    (activeContext.entities?.accounts?.length || 0) +
                    (activeContext.entities?.contacts?.length || 0) +
                    (activeContext.entities?.opportunities?.length || 0)
                  }
                  size="small"
                  style={{ backgroundColor: activeContext.color }}
                />
              </Space>
            </Button>
          </Popover>
        )}

        {/* Dashboard - always visible */}
        <Tooltip title="Dashboard">
          <Button
            type="text"
            icon={<HomeOutlined />}
            onClick={() => navigate("/")}
          />
        </Tooltip>

        {/* Dynamic module navigation - generated from registry, grouped */}
        {(() => {
          const items = getNavigationItems();
          let lastGroup = "";
          return items.map((item) => {
            const showDivider = lastGroup && (item.group || "") !== lastGroup;
            lastGroup = item.group || "";
            return (
              <React.Fragment key={item.name}>
                {showDivider && (
                  <div style={{
                    width: 2,
                    height: 22,
                    background: "#cbd5e1",
                    margin: "0 6px",
                    borderRadius: 1,
                  }} />
                )}
                <Tooltip title={item.label}>
                  <Button
                    type="text"
                    icon={iconComponents[item.icon] || <AppstoreOutlined />}
                    onClick={() => navigate(item.path)}
                  />
                </Tooltip>
              </React.Fragment>
            );
          });
        })()}

        <Tooltip title="Search (⌘K)">
          <Button type="text" icon={<SearchOutlined />} />
        </Tooltip>
        <MessagingInbox />
      </Space>
    </div>
  );
};
